import { qnToLastCalledAdapter } from "@dqm-custom/telia-last-called-utils";
import { HttpEnqueueActionsSource } from "@dqm/http-enqueue-actions-source";
import { HttpPollingSource } from "@dqm/http-polling-source";
import { HttpPrinterStatusSource } from "@dqm/http-printer-status-source";
import { QueueNumber } from "@dqm/queue-number";
import { ResolveActivityExpression } from "@dqm/resolve-activity-expression";
import {
  AdapterTransformer,
  ChangeObserver,
  JsonEqualityCheck,
  StrictEqualityCheck,
  ValueService,
} from "@dqm/value-observer";
import React from "react";
import ReactDOM from "react-dom";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "./scss/main.scss";
import { EnqueueService } from "./services/enqueue/EnqueueService";
import { PrintingService } from "./services/print";
import { IElectronAPI } from "./services/uiStatus/index.d";
import { UiStatusService } from "./services/uiStatus/UiStatusService";

const statusGroupsApiAddress =
  process.env.REACT_APP_HOST + "/queue-status/status-groups";
const statusGroupsSource = new HttpPollingSource<QueueNumber[]>(
  statusGroupsApiAddress,
  { pollInterval: 1000 }
);

const enqueueActionsApiAddress =
  process.env.REACT_APP_HOST + "/enqueue/actions";
const enqueueActionsSource = new HttpEnqueueActionsSource(
  enqueueActionsApiAddress,
  { pollInterval: 5000 }
);

const buildEnqueueService = () => {
  const enqueueService: EnqueueService = new EnqueueService();
  enqueueService.start();
  return enqueueService;
};

const buildPrinterStatusProvider = () => {
  const printerStatusApiAddress = "http://localhost:4404/print/status";
  const printerStatusSource = new HttpPrinterStatusSource(
    printerStatusApiAddress,
    { pollInterval: 5000 }
  );
  printerStatusSource.start();

  return new ChangeObserver(printerStatusSource, new StrictEqualityCheck());
};

const buildLastCalledProvider = () => {
  let currentStatus: QueueNumber[] = [];
  let showAnimation = false;
  const adapter = (statusGroups: QueueNumber[]) => {
    let next = null;

    if (showAnimation) {
      for (const queueNumber of statusGroups) {
        if (!containsQueueNumber(currentStatus, queueNumber)) {
          next = qnToLastCalledAdapter(queueNumber);
        }
      }
    }

    showAnimation = true;
    currentStatus = statusGroups;

    return {
      next,
      items: statusGroups.map(qnToLastCalledAdapter),
    };
  };

  return new ChangeObserver(
    new AdapterTransformer(adapter, statusGroupsSource),
    new JsonEqualityCheck()
  );
};

function containsQueueNumber(list: QueueNumber[], queueNumber: QueueNumber) {
  for (const entry of list) {
    if (entry.id === queueNumber.id) {
      return true;
    }
  }

  return false;
}

const buildEnqueueActionsProvider = () => {
  return new ChangeObserver(
    new ResolveActivityExpression(enqueueActionsSource),
    new JsonEqualityCheck()
  );
};

const buildValueService = async (): Promise<ValueService> => {
  const valueService = new ValueService();

  valueService.setProvider("printer-status", buildPrinterStatusProvider());
  valueService.setProvider("last-called", await buildLastCalledProvider());
  valueService.setProvider("enqueue-actions", buildEnqueueActionsProvider());

  return valueService;
};

const createNullElectronApi = () => {
  const electronApi = {
    reactIsReady: () => {},
    reactError: (errorMessage: string) => {},
  };

  return electronApi as unknown as IElectronAPI;
};

const start = async () => {
  const valueService = await buildValueService();
  const enqueueService = buildEnqueueService();
  const printingService = new PrintingService();
  const uiStatusService = new UiStatusService(
    window.electronAPI ?? createNullElectronApi()
  );

  statusGroupsSource.start();
  enqueueActionsSource.start();

  ReactDOM.render(
    <React.StrictMode>
      <App
        uiStatusService={uiStatusService}
        enqueueService={enqueueService}
        valueService={valueService}
        printingService={printingService}
      />
    </React.StrictMode>,
    document.getElementById("root")
  );
};

start();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
